import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { dataTestIds } from 'src/constants/data-test-ids';
import { VisitStatusLabel } from 'utils';

export const IN_PERSON_CHIP_STATUS_MAP: {
  [status in VisitStatusLabel]: {
    background: {
      primary: string;
      secondary?: string;
    };
    color: {
      primary: string;
      secondary?: string;
    };
  };
} = {
  pending: {
    background: {
      primary: '#EEF0F2',
    },
    color: {
      primary: '#4A4A5A',
    },
  },
  arrived: {
    background: {
      primary: '#DEEAF7',
      secondary: '#8aa0e1',
    },
    color: {
      primary: '#22222d',
    },
  },
  ready: {
    background: {
      primary: '#D6EAD7',
      secondary: '#2E7D32',
    },
    color: {
      primary: '#1B5E20',
    },
  },
  intake: {
    background: {
      primary: '#E8E4F5',
    },
    color: {
      primary: '#3a2d6e',
    },
  },
  'ready for provider': {
    background: {
      primary: '#D5E8F5',
      secondary: '#22222d',
    },
    color: {
      primary: '#0d3a5e',
    },
  },
  provider: {
    background: {
      primary: '#C8DFF7',
    },
    color: {
      primary: '#22222d',
    },
  },
  discharged: {
    background: {
      primary: '#C4EAE8',
    },
    color: {
      primary: '#00474A',
    },
  },
  completed: {
    background: {
      primary: '#F0F2F5',
    },
    color: {
      primary: '#4A4A5A',
    },
  },
  'awaiting supervisor approval': {
    background: {
      primary: '#FEF3E2',
    },
    color: {
      primary: '#7A4800',
    },
  },
  cancelled: {
    background: {
      primary: '#FCEAEA',
    },
    color: {
      primary: '#B71C1C',
    },
  },
  'no show': {
    background: {
      primary: '#EBEBED',
    },
    color: {
      primary: '#22222d',
    },
  },
  unknown: {
    background: {
      primary: '#F9F7F4',
    },
    color: {
      primary: '#22222d',
    },
  },
};

export function InPersonAppointmentStatusChip({
  status,
  count,
}: {
  status: VisitStatusLabel | undefined;
  count?: number;
}): ReactElement {
  const { t } = useTranslation();

  if (!status) {
    return <></>;
  }

  if (!IN_PERSON_CHIP_STATUS_MAP[status]) {
    return <></>;
  }

  const statusKey = status.toLowerCase().replace(/\s+/g, '_');
  const label = t(`visitStatus.${statusKey}`, { defaultValue: status });

  return (
    <span
      data-testid={dataTestIds.dashboard.appointmentStatus}
      style={{
        fontSize: '12px',
        borderRadius: '4px',
        border: `${['pending', 'checked out'].includes(status) ? '1px solid #BFC2C6' : 'none'}`,
        textTransform: 'uppercase',
        background: IN_PERSON_CHIP_STATUS_MAP[status].background.primary,
        color: IN_PERSON_CHIP_STATUS_MAP[status].color.primary,
        display: 'inline-block',
        padding: '2px 8px 0 8px',
        verticalAlign: 'middle',
      }}
    >
      {count ? `${label} - ${count}` : label}
    </span>
  );
}
